"use client";

import { Fragment } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import { visibleNavLinks, type NavKey } from "@/lib/nav-links";

// Every row in the mobile menu — nav links and "Log in" alike — shares this
// typography/height so the login row reads as one of the menu items rather
// than a bolt-on.
const ROW_CLASS =
  "flex h-10 items-center justify-center font-[family-name:var(--font-rubik),sans-serif] text-base font-medium leading-6 tracking-normal transition-opacity hover:opacity-80";

// Shared link list for the full-screen mobile hamburger menu. Used by the
// AppHeader overlay (templates, community, projects, referrals, profile, …)
// and the home/pricing overlays so every page's menu has the same links,
// order, typography, spacing, and active-state styling.
//
// Signed-out visitors get a "Log in" row directly under Home. It used to be
// hand-added at the *bottom* of the home + pricing overlays and was missing
// from the AppHeader menu entirely (templates, community), so owning it here
// is what stops that drift from happening again.
export function MobileNavLinks({
  signedIn,
  activeKey,
  onNavigate,
  onLogin,
}: {
  signedIn: boolean;
  activeKey?: NavKey;
  onNavigate?: () => void;
  /**
   * Opens the page's own login modal. When omitted, the row falls back to the
   * /login route (which reopens the modal on the home page and returns here
   * afterwards) so no page can render a dead "Log in".
   */
  onLogin?: () => void;
}) {
  const pathname = usePathname();
  const links = visibleNavLinks(signedIn);

  // "Log in" sits directly below Home. findIndex rather than a hardcoded 0 so
  // the row stays anchored to Home if the nav order ever changes; -1 (no Home
  // link visible) falls back to the top of the list.
  const homeIndex = links.findIndex((link) => link.key === "home");
  const loginAfterIndex = homeIndex === -1 ? 0 : homeIndex;

  const loginHref =
    pathname && pathname !== "/"
      ? `/login?next=${encodeURIComponent(pathname)}`
      : "/login";

  const loginRow = onLogin ? (
    <button
      type="button"
      onClick={() => {
        onNavigate?.();
        onLogin();
      }}
      className={cn(ROW_CLASS, "text-[#1A1A1B] dark:text-foreground")}
    >
      Log in
    </button>
  ) : (
    <Link
      href={loginHref}
      onClick={onNavigate}
      className={cn(ROW_CLASS, "text-[#1A1A1B] dark:text-foreground")}
    >
      Log in
    </Link>
  );

  return (
    <nav className="mt-2 flex flex-col gap-2 px-4">
      {links.map((link, index) => (
        <Fragment key={link.href}>
          <Link
            href={link.href}
            onClick={onNavigate}
            className={cn(
              ROW_CLASS,
              activeKey === link.key
                ? "text-orange-600 dark:text-orange-400"
                : "text-[#1A1A1B] dark:text-foreground",
            )}
          >
            {link.label}
          </Link>
          {!signedIn && index === loginAfterIndex && loginRow}
        </Fragment>
      ))}
    </nav>
  );
}
